import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  FileText,
  Shield,
  Upload,
  User,
  X,
} from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { fileToBase64 } from "../../utils/claimHelpers";
import ClaimStepper, { REPRESENTATIVE_CLAIM_STEPS } from "../../components/claims/ClaimStepper";

/* ─── constants ─────────────────────────────────────────────── */
const RELATIONSHIP_OPTIONS = [
  "Spouse", "Child", "Parent", "Sibling",
  "Legal guardian", "Authorized representative", "Other",
];

/* ─── shared styles ─────────────────────────────────────────── */
const inputBase =
  "w-full h-12 px-3.5 text-sm rounded-xl border bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300";
const inputNormal  = `${inputBase} border-gray-200`;
const inputError   = `${inputBase} border-red-400 bg-red-50 focus:ring-red-200`;
const labelClass   = "block text-xs font-semibold text-gray-700 mb-1.5";

/* ─── tiny helpers ──────────────────────────────────────────── */
const Required = () => <span className="ml-0.5 text-red-500">*</span>;
const Optional  = () => <span className="ml-1 text-[10px] font-normal text-gray-400">(optional)</span>;

const FieldError = ({ message }) =>
  message ? (
    <motion.p
      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
      className="flex gap-1 items-center mt-1 text-xs text-red-600"
    >
      <AlertCircle size={11} className="shrink-0" /> {message}
    </motion.p>
  ) : null;

/* ─── ID card frame ─────────────────────────────────────────── */
const idCardFrame =
  "relative w-full max-w-[260px] overflow-hidden rounded-xl aspect-[85.6/53.98]";

const FilePreviewCard = ({ file, label, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const isDataUrl = typeof file === "string";
  const isImage = isDataUrl
    ? file.startsWith("data:image")
    : file?.type?.startsWith("image/");

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return undefined; }
    if (isDataUrl) { setPreviewUrl(isImage ? file : null); return undefined; }
    if (!isImage)  { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isDataUrl, isImage]);

  if (!file) return null;

  return (
    <div className={`${idCardFrame} border border-gray-200 bg-slate-100 shadow-sm`}>
      {previewUrl ? (
        <img src={previewUrl} alt={label}
          title={isDataUrl ? label : file.name}
          className="object-contain w-full h-full bg-white" />
      ) : (
        <div className="flex flex-col justify-center items-center w-full h-full text-gray-400 bg-white">
          <FileText size={28} /><span className="mt-1 text-xs font-medium">PDF</span>
        </div>
      )}
      <span className="absolute left-2 top-2 rounded-md bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-700 shadow-sm">
        {label}
      </span>
      <button type="button" onClick={onRemove}
        className="absolute top-2 right-2 grid place-items-center w-7 h-7 text-red-600 bg-white rounded-lg shadow-sm hover:bg-red-50"
        aria-label={`Remove ${label}`}>
        <X size={14} />
      </button>
    </div>
  );
};

const IdUploadSlot = ({ file, label, hint, onChange, onRemove, error, slotRef }) => (
  <div ref={slotRef}>
    <span className={labelClass}>{label} <Required /></span>
    {file ? (
      <div className="mt-0.5">
        <FilePreviewCard file={file} label={label} onRemove={onRemove} />
        <button type="button" onClick={onRemove}
          className="flex gap-1 items-center mt-2 text-xs font-medium text-red-500 hover:text-red-700">
          <X size={12} /> Remove
        </button>
      </div>
    ) : (
      <label className="block mt-0.5 cursor-pointer group">
        <div className={`${idCardFrame} flex flex-col justify-center items-center px-4 border-2 border-dashed
          transition-colors
          ${error ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50 group-hover:border-emerald-400 group-hover:bg-emerald-50/30"}`}>
          <Upload size={20} className={`transition-colors ${error ? "text-red-400" : "text-gray-400 group-hover:text-emerald-500"}`} />
          <p className={`mt-1.5 text-xs font-medium text-center ${error ? "text-red-600" : "text-gray-500 group-hover:text-emerald-700"}`}>{hint}</p>
          <p className="mt-0.5 text-[10px] text-gray-400">PNG · JPG · PDF · 2 MB</p>
        </div>
        <input type="file" accept="image/*,application/pdf" className="sr-only"
          onChange={(e) => { onChange(e.target.files?.[0] || null); e.target.value = ""; }} />
      </label>
    )}
    <FieldError message={error} />
  </div>
);

/* ─── sidebar stepper ───────────────────────────────────────── */
const SidebarStepper = ({ steps, currentIndex, policyName }) => (
  <aside className="hidden lg:block w-64 shrink-0">
    <div className="fixed top-28 w-64 space-y-6">
      <div>
        <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-4">
          Claim progress
        </p>
        <ol className="space-y-1">
          {steps.map((label, idx) => {
            const done   = idx < currentIndex;
            const active = idx === currentIndex;
            // On this page nothing is "done" yet (currentIndex=0), so no clicks
            return (
              <li key={label} className="flex gap-3 items-start">
                <div className="flex flex-col items-center mt-0.5">
                  <div className={`grid place-items-center w-7 h-7 rounded-full text-xs font-bold shrink-0 transition-all ${
                    done    ? "bg-emerald-600 text-white shadow-sm shadow-emerald-200"
                    : active ? "bg-emerald-600 text-white ring-4 ring-emerald-100"
                    : "bg-gray-100 text-gray-400"
                  }`}>
                    {done ? <CheckCircle2 size={14} /> : idx + 1}
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`w-px flex-1 min-h-[28px] mt-1 ${done ? "bg-emerald-300" : "bg-gray-200"}`} />
                  )}
                </div>
                <div className="pb-5">
                  <p className={`text-sm leading-tight transition-colors ${
                    active ? "font-bold text-gray-900"
                    : done  ? "font-semibold text-emerald-700"
                    : "text-gray-400"
                  }`}>{label}</p>
                  {active && (
                    <p className="text-[11px] text-emerald-600 font-medium mt-0.5">Current step</p>
                  )}
                  {!active && !done && (
                    <p className="text-[11px] text-gray-300 mt-0.5">Complete this step first</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {policyName && (
        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-3">
          <div className="flex gap-2 items-center">
            <Shield size={16} className="text-emerald-600 shrink-0" />
            <p className="text-xs font-bold text-gray-700">Policy</p>
          </div>
          {policyName && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Cover</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">{policyName}</p>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
        <p className="text-xs font-semibold text-amber-800 flex gap-1.5 items-center">
          <AlertCircle size={13} /> Third-party claim
        </p>
        <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
          You are claiming on someone else's behalf. Ensure your ID matches the uploaded document.
        </p>
      </div>
    </div>
  </aside>
);

/* ─── validation ─────────────────────────────────────────────── */
const toBase64 = async (file) => {
  if (!file) return null;
  if (typeof file === "string") return file;
  return fileToBase64(file);
};

const validate = (values) => {
  const errors = {};
  if (!values.relationship)       errors.relationship = "Please select your relationship to the policyholder";
  if (!values.idNumber.trim())    errors.idNumber     = "Ghana Card ID is required";
  if (!values.idFront)            errors.idFront      = "Please upload the front of your ID card";
  if (!values.idBack)             errors.idBack       = "Please upload the back of your ID card";
  return errors;
};

/* field → ref key map (for scroll-to-error) */
const FIELD_ORDER = ["relationship", "idNumber", "idFront", "idBack"];

/* ─── main component ─────────────────────────────────────────── */
const RelativeClaimForm = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const policy     = location.state?.policy;
  const savedRep   = location.state?.representative;
  const user       = useAppSelector((s) => s.auth.user);
  const [isPreparing, setIsPreparing] = useState(false);

  /* refs for scroll-to-error */
  const fieldRefs = useRef({});
  const setFieldRef = useCallback((name) => (el) => {
    if (el) fieldRefs.current[name] = el;
  }, []);

  const initialValues = useMemo(() => ({
    fullName:     savedRep?.full_name     || "",
    relationship: savedRep?.relationship  || "",
    phone:        savedRep?.phone         || "",
    idNumber:     savedRep?.id_number     || "",
    idFront:      savedRep?.id_front      || null,
    idBack:       savedRep?.id_back       || null,
  }), [savedRep]);

  if (!policy) return <Navigate to="/claim/new" replace />;

  const scrollToFirstError = (errors) => {
    for (const key of FIELD_ORDER) {
      if (errors[key] && fieldRefs.current[key]) {
        fieldRefs.current[key].scrollIntoView({ behavior: "smooth", block: "center" });
        const input = fieldRefs.current[key].querySelector("input,select,textarea");
        if (input) input.focus({ preventScroll: true });
        break;
      }
    }
  };

  const handleSubmit = async (values, formik) => {
    const errors = validate(values);
    formik.setTouched(
      FIELD_ORDER.reduce((a, k) => ({ ...a, [k]: true }), {}),
      false
    );
    formik.setErrors(errors);
    if (Object.keys(errors).length > 0) {
      scrollToFirstError(errors);
      return;
    }
    setIsPreparing(true);
    try {
      const representative = {
        full_name:         values.fullName.trim(),
        relationship:      values.relationship,
        phone:             values.phone.trim(),
        email:             user?.email || "",
        id_type:           "Ghana Card",
        id_number:         values.idNumber.trim(),
        id_front:          await toBase64(values.idFront),
        id_back:           await toBase64(values.idBack),
      };
      navigate("/claim/new/form", {
        state: { policy, forSelf: false, representative },
      });
    } finally {
      setIsPreparing(false);
    }
  };

  const policyName = policy?.provider?.name;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="pt-20" />

      {/* mobile stepper */}
      <div className="lg:hidden px-4 pt-4 sm:px-6">
        <ClaimStepper steps={REPRESENTATIVE_CLAIM_STEPS} currentIndex={0} />
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="flex gap-10 items-start pt-8">
          {/* sidebar */}
          <SidebarStepper
            steps={REPRESENTATIVE_CLAIM_STEPS}
            currentIndex={0}
            policyName={policyName}
          />

          {/* main content */}
          <div className="flex-1 min-w-0">
            {/* page header */}
            <div className="flex gap-4 justify-between items-start mb-6">
              <div className="flex flex-1 gap-4 items-start min-w-0">
                <div className="grid place-items-center w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 text-white shadow-lg shadow-orange-200 shrink-0">
                  <User size={22} />
                </div>
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl leading-tight">
                    Claim on behalf of policyholder
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Verify your identity before we proceed with the claim
                    {policyName ? ` for ${policyName}` : ""}.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="group inline-flex items-center gap-2 h-10 sm:h-11 shrink-0 rounded-full border border-gray-200 bg-white pl-1.5 pr-4 text-sm font-semibold text-gray-700 shadow-sm hover:border-orange-300 hover:bg-orange-50 hover:text-orange-800 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2"
              >
                <span className="grid place-items-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 transition-colors group-hover:bg-orange-100 group-hover:text-orange-700">
                  <ChevronLeft size={16} aria-hidden="true" />
                </span>
                Back
              </button>
            </div>

            <Formik
              initialValues={initialValues}
              enableReinitialize
              validate={validate}
              validateOnChange={false}
              validateOnBlur={true}
              onSubmit={() => {}}
            >
              {(formik) => {
                const hasErr = (field) =>
                  !!(formik.touched[field] && formik.errors[field]);

                return (
                  <form onSubmit={(e) => { e.preventDefault(); handleSubmit(formik.values, formik); }}
                    className="space-y-6">

                    {/* ── Personal info card ── */}
                    <motion.section
                      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}>
                      <div className="px-6 pt-5 pb-2 border-b border-gray-50">
                        <div className="flex gap-2 items-center">
                          <User size={16} className="text-orange-500" />
                          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                            Personal information
                          </h2>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Your details as the representative claimant</p>
                      </div>
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 px-6 py-5">
                        <div className="min-w-0">
                          <label className={labelClass}>
                            Full name <Optional />
                          </label>
                          <input
                            className={inputNormal}
                            placeholder="e.g. Kwame Boateng"
                            value={formik.values.fullName}
                            onChange={formik.handleChange("fullName")}
                          />
                        </div>

                        <div ref={setFieldRef("relationship")} className="min-w-0">
                          <label className={labelClass}>
                            Relationship to insured <Required />
                          </label>
                          <div className="relative">
                            <select
                              className={`${hasErr("relationship") ? inputError : inputNormal} appearance-none pr-9`}
                              value={formik.values.relationship}
                              onChange={formik.handleChange("relationship")}
                              onBlur={formik.handleBlur("relationship")}
                            >
                              <option value="">Select relationship…</option>
                              {RELATIONSHIP_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                            <ChevronDown size={16}
                              className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" />
                          </div>
                          <FieldError message={hasErr("relationship") && formik.errors.relationship} />
                        </div>

                        <div className="min-w-0">
                          <label className={labelClass}>
                            Phone number <Optional />
                          </label>
                          <input type="tel"
                            className={inputNormal}
                            placeholder="0244 000 000"
                            value={formik.values.phone}
                            onChange={formik.handleChange("phone")}
                          />
                        </div>

                        <div ref={setFieldRef("idNumber")} className="min-w-0">
                          <label className={labelClass}>
                            Ghana Card ID <Required />
                          </label>
                          <input
                            className={hasErr("idNumber") ? inputError : inputNormal}
                            placeholder="GHA-000000000-0"
                            value={formik.values.idNumber}
                            onChange={formik.handleChange("idNumber")}
                            onBlur={formik.handleBlur("idNumber")}
                          />
                          <FieldError message={hasErr("idNumber") && formik.errors.idNumber} />
                        </div>
                      </div>
                    </motion.section>

                    {/* ── ID card upload card ── */}
                    <motion.section
                      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.08 }}>
                      <div className="px-6 pt-5 pb-2 border-b border-gray-50">
                        <div className="flex gap-2 items-center">
                          <Upload size={16} className="text-orange-500" />
                          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                            ID card upload
                          </h2>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Upload both sides of your Ghana Card so we can verify your identity
                        </p>
                      </div>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 px-6 py-5">
                        <IdUploadSlot
                          file={formik.values.idFront}
                          label="Front"
                          hint="Upload ID card front"
                          onChange={(f) => formik.setFieldValue("idFront", f)}
                          onRemove={() => formik.setFieldValue("idFront", null)}
                          error={hasErr("idFront") && formik.errors.idFront}
                          slotRef={setFieldRef("idFront")}
                        />
                        <IdUploadSlot
                          file={formik.values.idBack}
                          label="Back"
                          hint="Upload ID card back"
                          onChange={(f) => formik.setFieldValue("idBack", f)}
                          onRemove={() => formik.setFieldValue("idBack", null)}
                          error={hasErr("idBack") && formik.errors.idBack}
                          slotRef={setFieldRef("idBack")}
                        />
                      </div>

                      {/* validation banner */}
                      <AnimatePresence>
                        {Object.keys(validate(formik.values)).length > 0 &&
                          Object.keys(formik.touched).length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mx-6 mb-5 flex gap-2 items-start rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                            <AlertCircle size={15} className="text-red-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-red-700">Complete all required fields</p>
                              <ul className="mt-0.5 space-y-0.5">
                                {FIELD_ORDER.filter((k) => validate(formik.values)[k]).map((k) => (
                                  <li key={k} className="text-[11px] text-red-600">
                                    {validate(formik.values)[k]}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.section>

                    {/* ── footer actions ── */}
                    <motion.div
                      className="flex justify-end items-center gap-3 pt-2"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}>
                      <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="group inline-flex items-center gap-2 h-11 rounded-full border border-gray-200 bg-white pl-1.5 pr-4 text-sm font-semibold text-gray-700 shadow-sm hover:border-orange-300 hover:bg-orange-50 hover:text-orange-800 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2"
                      >
                        <span className="grid place-items-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 transition-colors group-hover:bg-orange-100 group-hover:text-orange-700">
                          <ChevronLeft size={16} aria-hidden="true" />
                        </span>
                        Back
                      </button>
                      <button type="submit" disabled={isPreparing}
                        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3 text-sm font-bold text-white shadow-md shadow-orange-200 hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 transition-all">
                        {isPreparing ? (
                          <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Preparing…</>
                        ) : (
                          <>Continue to incident details →</>
                        )}
                      </button>
                    </motion.div>
                  </form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelativeClaimForm;
